import { AsyncPanel } from '../ui/AsyncPanel'
import type { ResourceState } from '../data/resource'
import type { CartView } from '../data/endpoints'
import { CustomerShell, rupees } from './CustomerShell'

/**
 * The cart, with every number the server computed and none it did not.
 *
 * Totals are read straight off the CartView. The temptation is to add the lines up
 * in the browser for a snappier feel, and that is exactly how a customer ends up
 * seeing a total that differs from the one they are charged - shipping, credit and
 * rounding all live in the domain, so the browser's only job is to print them.
 *
 * Remove is a destructive control, so it sits on the row at the top of the reach
 * arc rather than next to the checkout button. Nobody loses a book by fat-thumbing
 * the wrong control at the bottom of the screen.
 */
export interface CartProps {
  state: ResourceState<CartView>
  creditPaise: number | null
  onRemove?: (lineId: string) => void
  onCheckout?: () => void
  onRetry?: () => void
  checkingOut?: boolean
  online?: boolean
}

export function Cart({ state, creditPaise, onRemove, onCheckout, onRetry, checkingOut = false, online }: CartProps) {
  const cart = state.status === 'ready' ? state.data : null

  return (
    <CustomerShell
      title="Your cart"
      creditPaise={creditPaise}
      cart={cart ? { lines: cart.lines.length, payablePaise: cart.payablePaise } : null}
      online={online}
      primaryAction={
        cart
          ? {
              label: checkingOut ? 'Placing...' : `Pay ${rupees(cart.payablePaise)}`,
              disabled: checkingOut || cart.lines.length === 0,
              onPress: onCheckout,
            }
          : undefined
      }
    >
      <AsyncPanel state={state} emptyMessage="Your cart is empty - have a browse" onRetry={onRetry}>
        {(c) => (
          <>
            <ul className="sf-lines">
              {c.lines.map((line) => (
                <li key={line.id} className="sf-line">
                  <span className="sf-line-name">{line.titleName ?? line.unitId}</span>
                  <span className="sf-line-qty">x{line.qty}</span>
                  <span className="sf-line-total">{rupees(line.lineTotalPaise)}</span>
                  <button type="button" className="btn btn-danger reach-top" onClick={() => onRemove?.(line.id)}>
                    Remove
                  </button>
                </li>
              ))}
            </ul>

            <dl className="sf-totals">
              <dt>Subtotal</dt>
              <dd>{rupees(c.subtotalPaise)}</dd>
              <dt>Shipping</dt>
              <dd>{rupees(c.shippingPaise)}</dd>
              {c.creditAppliedPaise > 0 ? (
                <>
                  <dt>Credit applied</dt>
                  <dd>-{rupees(c.creditAppliedPaise)}</dd>
                </>
              ) : null}
              <dt className="sf-payable-label">To pay</dt>
              <dd className="sf-payable t-display">{rupees(c.payablePaise)}</dd>
            </dl>
          </>
        )}
      </AsyncPanel>
    </CustomerShell>
  )
}
